// BHP-1063: DNS management for one domain — list, search, add, edit, delete.
// Replaces the legacy DnsZoneOverview (react-bootstrap on `/api/v1/dns/*`).
//
// `syncStatus` is surfaced per record on purpose: a write can succeed while the
// Cloudflare sync is still pending or has failed (BHP-1011), and a customer who
// only sees "saved" would assume the record is live.
import { useState } from 'react';
import {
  DNS_RECORD_FIELDS,
  type DnsRecord,
  type DnsRecordInput,
  type DnsSyncStatus,
  isReadOnlyType,
  TTL_DEFAULT
} from '../dns';
import { useDnsRecords, useDnsZone } from '../application/dnsQueries';
import {
  useCreateDnsRecord,
  useDeleteDnsRecord,
  useUpdateDnsRecord
} from '../application/dnsMutations';
import DnsRecordForm from './DnsRecordForm';
import { toFormErrors, toUserMessage } from '../../../shared/http/errorMessage';
import InlineConfirm from '../../../shared/ui/InlineConfirm';

const SYNC_STYLES: Record<DnsSyncStatus, string> = {
  synced: 'bg-teal-50 text-teal-700',
  pending: 'bg-amber-50 text-amber-700',
  failed: 'bg-rose-50 text-rose-700'
};
const SYNC_LABELS: Record<DnsSyncStatus, string> = {
  synced: 'Live',
  pending: 'Syncing',
  failed: 'Not synced'
};

function SyncBadge({ status }: { status: DnsSyncStatus }): JSX.Element {
  const style = SYNC_STYLES[status] ?? 'bg-slate-100 text-slate-600';
  return (
    <span className={`rounded px-2 py-0.5 text-xs font-medium ${style}`}>
      {SYNC_LABELS[status] ?? status}
    </span>
  );
}

function toInput(record: DnsRecord): DnsRecordInput {
  return {
    type: record.type,
    name: record.name,
    value: record.value,
    priority: record.priority,
    ttl: record.ttl ?? TTL_DEFAULT
  };
}

export default function DnsZonePanel({
  dnsZoneUuid
}: {
  dnsZoneUuid: string | null;
}): JSX.Element {
  const [search, setSearch] = useState('');
  const [isAdding, setIsAdding] = useState(false);
  const [editing, setEditing] = useState<DnsRecord | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  // BHP-1048: 422 violations split per input; the banner keeps whatever could
  // not be bound to a field the form renders.
  const [formFieldErrors, setFormFieldErrors] = useState<
    Record<string, string>
  >({});
  const [rowError, setRowError] = useState<string | null>(null);
  // Deleting a record is destructive and irreversible at Cloudflare, so it asks
  // first. `deletingUuid` then disables that row while the request is in
  // flight: the row only disappears once the refetch lands, and without this
  // the button stayed clickable and could fire the same delete twice.
  const [confirmingUuid, setConfirmingUuid] = useState<string | null>(null);
  const [deletingUuid, setDeletingUuid] = useState<string | null>(null);

  const zone = useDnsZone(dnsZoneUuid);
  const records = useDnsRecords(dnsZoneUuid, search);
  const { create, isPending: isCreating } = useCreateDnsRecord(
    dnsZoneUuid ?? ''
  );
  const { update, isPending: isUpdating } = useUpdateDnsRecord();
  const { remove } = useDeleteDnsRecord();

  if (dnsZoneUuid === null) {
    return (
      <p className="text-sm text-slate-500">
        This domain has no DNS zone yet. A zone is created once the registration
        completes.
      </p>
    );
  }

  const clearFormErrors = (): void => {
    setFormError(null);
    setFormFieldErrors({});
  };

  const showFormError = (error: unknown, fallback: string): void => {
    const { fields, message } = toFormErrors(
      error,
      fallback,
      DNS_RECORD_FIELDS
    );
    setFormFieldErrors(fields);
    setFormError(message);
  };

  const closeForm = (): void => {
    setIsAdding(false);
    setEditing(null);
    clearFormErrors();
  };

  const onCreate = (input: DnsRecordInput): void => {
    clearFormErrors();
    create(input)
      .then(closeForm)
      .catch((error: unknown) => {
        showFormError(error, 'Could not add this record.');
      });
  };

  const onUpdate = (input: DnsRecordInput): void => {
    if (editing === null) {
      return;
    }
    clearFormErrors();
    update({ uuid: editing.uuid, input })
      .then(closeForm)
      .catch((error: unknown) => {
        showFormError(error, 'Could not save this record.');
      });
  };

  const onDelete = (record: DnsRecord): void => {
    setRowError(null);
    setDeletingUuid(record.uuid);
    remove(record.uuid)
      .then(() => {
        setConfirmingUuid(null);
      })
      .catch((error: unknown) => {
        setRowError(toUserMessage(error, 'Could not delete this record.'));
      })
      .finally(() => {
        setDeletingUuid(null);
      });
  };

  const rows = records.data?.items ?? [];

  return (
    <section className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h3 className="text-base font-semibold text-slate-900">DNS records</h3>
        <div className="flex items-center gap-2">
          <input
            type="search"
            value={search}
            onChange={(event) => {
              setSearch(event.currentTarget.value);
            }}
            placeholder="Search by name…"
            aria-label="Search DNS records"
            className="rounded-md border border-solid border-slate-300 px-3 py-1.5 text-sm text-slate-800 focus:border-teal-500 focus:outline-none"
          />
          <button
            type="button"
            onClick={() => {
              setEditing(null);
              clearFormErrors();
              setIsAdding(true);
            }}
            className="rounded-md border-0 bg-teal-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-teal-700"
          >
            New record
          </button>
        </div>
      </div>

      {zone.isError ? (
        <p
          role="alert"
          className="rounded-lg border border-solid border-rose-200 bg-rose-50 p-3 text-sm text-rose-700"
        >
          {toUserMessage(zone.error, 'Could not load the DNS zone.')}
        </p>
      ) : null}

      {isAdding ? (
        <DnsRecordForm
          heading="New DNS record"
          submitLabel="Add record"
          isSubmitting={isCreating}
          serverError={formError}
          serverFieldErrors={formFieldErrors}
          onSubmit={onCreate}
          onCancel={closeForm}
        />
      ) : null}

      {editing !== null ? (
        <DnsRecordForm
          initial={toInput(editing)}
          heading={`Edit ${editing.type} record`}
          submitLabel="Save record"
          isSubmitting={isUpdating}
          serverError={formError}
          serverFieldErrors={formFieldErrors}
          onSubmit={onUpdate}
          onCancel={closeForm}
        />
      ) : null}

      {rowError !== null ? (
        <p
          role="alert"
          className="rounded-lg border border-solid border-rose-200 bg-rose-50 p-3 text-sm text-rose-700"
        >
          {rowError}
        </p>
      ) : null}

      {records.isPending ? (
        <p className="text-sm text-slate-500">Loading records…</p>
      ) : records.isError ? (
        <p
          role="alert"
          className="rounded-lg border border-solid border-rose-200 bg-rose-50 p-3 text-sm text-rose-700"
        >
          {toUserMessage(records.error, 'Could not load the DNS records.')}
        </p>
      ) : rows.length === 0 ? (
        <p className="rounded-xl border border-solid border-slate-200 bg-white p-6 text-center text-sm text-slate-500">
          {search === ''
            ? 'No DNS records for this zone.'
            : `No records match “${search}”.`}
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[36rem] border-collapse overflow-hidden rounded-xl border border-solid border-slate-200 bg-white text-sm">
            <thead>
              <tr className="bg-slate-50 text-left text-xs uppercase text-slate-500">
                <th className="px-4 py-2 font-medium">Type</th>
                <th className="px-4 py-2 font-medium">Name</th>
                <th className="px-4 py-2 font-medium">Value</th>
                <th className="px-4 py-2 font-medium">TTL</th>
                <th className="px-4 py-2 font-medium">Status</th>
                <th className="px-4 py-2" />
              </tr>
            </thead>
            <tbody>
              {rows.map((record) => (
                <tr
                  key={record.uuid}
                  className="border-0 border-t border-solid border-slate-100"
                >
                  <td className="px-4 py-2 font-medium text-slate-800">
                    {record.type}
                  </td>
                  <td className="px-4 py-2 text-slate-700">{record.name}</td>
                  <td className="max-w-xs truncate px-4 py-2 text-slate-700">
                    {record.value}
                  </td>
                  <td className="px-4 py-2 text-slate-500">{record.ttl}</td>
                  <td className="px-4 py-2">
                    <SyncBadge status={record.syncStatus} />
                  </td>
                  <td className="px-4 py-2 text-right">
                    {isReadOnlyType(record.type) ? (
                      <span
                        title="Nameserver records are managed for you."
                        className="text-xs text-slate-400"
                      >
                        Managed
                      </span>
                    ) : confirmingUuid === record.uuid ? (
                      <InlineConfirm
                        layout="inline"
                        size="sm"
                        className="justify-end"
                        prompt={
                          <span className="text-xs text-slate-500">
                            Delete
                            <span className="sr-only">
                              {' '}
                              {record.type} record {record.name}
                            </span>
                            ?
                          </span>
                        }
                        confirmLabel="Confirm"
                        pendingLabel="Deleting…"
                        cancelLabel="Keep"
                        isPending={deletingUuid === record.uuid}
                        onConfirm={() => {
                          onDelete(record);
                        }}
                        onCancel={() => {
                          setConfirmingUuid(null);
                        }}
                      />
                    ) : (
                      <span className="flex justify-end gap-2">
                        <button
                          type="button"
                          disabled={deletingUuid !== null}
                          onClick={() => {
                            setIsAdding(false);
                            clearFormErrors();
                            setEditing(record);
                          }}
                          className="rounded-md border border-solid border-slate-300 bg-white px-2 py-1 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          disabled={deletingUuid !== null}
                          onClick={() => {
                            setRowError(null);
                            setConfirmingUuid(record.uuid);
                          }}
                          className="rounded-md border border-solid border-rose-300 bg-white px-2 py-1 text-xs font-medium text-rose-700 hover:bg-rose-50 disabled:opacity-50"
                        >
                          Delete
                        </button>
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
